import { useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type TrainerDto, type TrainerBookingDto, type TrainerUnavailabilityDto, type DayOfWeekName } from '@/api/admin'
import { useTopBar } from '@/contexts/TopBarContext'
import { Breadcrumb } from '@/components/shared/Breadcrumb'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { User, GraduationCap, Calendar, Plus, Trash2, ChevronRight, CalendarOff, X } from 'lucide-react'
import { useAdminDates } from '@/hooks/useDates'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Booked:    'bg-blue-100 text-blue-700',
    Completed: 'bg-green-100 text-green-700',
    Cancelled: 'bg-gray-100 text-gray-500',
    Rejected:  'bg-red-100 text-red-700',
  }
  return (
    <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${map[status] ?? 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  )
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = 'details' | 'qualifications' | 'availability' | 'sessions'

const TABS: { id: Tab; label: string; icon: typeof User }[] = [
  { id: 'details',        label: 'Details',        icon: User },
  { id: 'qualifications', label: 'Qualifications',  icon: GraduationCap },
  { id: 'availability',   label: 'Availability',    icon: CalendarOff },
  { id: 'sessions',       label: 'Sessions',        icon: Calendar },
]

// ─── Details tab ─────────────────────────────────────────────────────────────

function DetailsTab({ trainer }: { trainer: TrainerDto }) {
  const queryClient = useQueryClient()
  const [firstName, setFirstName] = useState(trainer.firstName)
  const [lastName,  setLastName]  = useState(trainer.lastName)
  const [email,     setEmail]     = useState(trainer.email)
  const [phone,     setPhone]     = useState(trainer.phone ?? '')
  const [isActive,  setIsActive]  = useState(trainer.isActive)
  const [saved,     setSaved]     = useState(false)

  const saveMutation = useMutation({
    mutationFn: () => adminApi.updateTrainer(trainer.id, {
      firstName, lastName, email,
      phone: phone.trim() || null,
      isActive,
      qualifiedCourseIds: trainer.qualifiedCourseIds,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainer', trainer.id] })
      queryClient.invalidateQueries({ queryKey: ['trainers'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    saveMutation.mutate()
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profile</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">First name</Label>
              <Input id="firstName" value={firstName} onChange={e => setFirstName(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Last name</Label>
              <Input id="lastName" value={lastName} onChange={e => setLastName(e.target.value)} required />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Optional" />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="isActive"
              type="checkbox"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <Label htmlFor="isActive" className="mb-0 cursor-pointer">Active</Label>
          </div>

          {saveMutation.isError && (
            <p className="text-sm text-red-600">Failed to save. Please try again.</p>
          )}

          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            {saved && <span className="text-sm text-green-600">Saved</span>}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

// ─── Qualifications tab ───────────────────────────────────────────────────────

function QualificationsTab({ trainer }: { trainer: TrainerDto }) {
  const queryClient = useQueryClient()
  const [addingId, setAddingId] = useState('')

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })

  const qualifiedCourses = courses.filter(c => trainer.qualifiedCourseIds.includes(c.id))
  const unqualifiedCourses = courses.filter(c => !trainer.qualifiedCourseIds.includes(c.id))

  const updateQuals = useMutation({
    mutationFn: (ids: string[]) => adminApi.updateTrainer(trainer.id, {
      firstName: trainer.firstName,
      lastName:  trainer.lastName,
      email:     trainer.email,
      phone:     trainer.phone,
      isActive:  trainer.isActive,
      qualifiedCourseIds: ids,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainer', trainer.id] })
      queryClient.invalidateQueries({ queryKey: ['trainers'] })
    },
  })

  const addCourse = () => {
    if (!addingId) return
    updateQuals.mutate([...trainer.qualifiedCourseIds, addingId])
    setAddingId('')
  }

  const removeCourse = (id: string) => {
    updateQuals.mutate(trainer.qualifiedCourseIds.filter(q => q !== id))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Qualified to deliver</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {qualifiedCourses.length === 0 ? (
          <p className="text-sm text-slate-500">No qualifications added yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
            {qualifiedCourses.map(course => (
              <li key={course.id} className="flex items-center justify-between px-4 py-2.5">
                <div>
                  <span className="text-sm font-medium text-slate-800">{course.name}</span>
                  {course.code && (
                    <span className="ml-2 text-xs text-slate-400">{course.code}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeCourse(course.id)}
                  disabled={updateQuals.isPending}
                  className="text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                >
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {unqualifiedCourses.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={addingId}
              onChange={e => setAddingId(e.target.value)}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="">Add a course qualification…</option>
              {unqualifiedCourses.map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ''}</option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addCourse}
              disabled={!addingId || updateQuals.isPending}
            >
              <Plus size={15} className="mr-1" /> Add
            </Button>
          </div>
        )}

        {updateQuals.isError && (
          <p className="text-sm text-red-600">Failed to update. Please try again.</p>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Availability tab ─────────────────────────────────────────────────────────

const ALL_DAYS: { name: DayOfWeekName; label: string }[] = [
  { name: 'Monday', label: 'Mon' }, { name: 'Tuesday', label: 'Tue' }, { name: 'Wednesday', label: 'Wed' },
  { name: 'Thursday', label: 'Thu' }, { name: 'Friday', label: 'Fri' }, { name: 'Saturday', label: 'Sat' }, { name: 'Sunday', label: 'Sun' },
]

/** Standalone rows group as themselves; rows sharing a RecurrenceGroupId group together so a
 * recurring pattern shows as one entry in the list, not one row per generated day. */
function groupUnavailability(rows: TrainerUnavailabilityDto[]): TrainerUnavailabilityDto[][] {
  const groups = new Map<string, TrainerUnavailabilityDto[]>()
  for (const r of rows) {
    const key = r.recurrenceGroupId ?? r.id
    const arr = groups.get(key) ?? []
    arr.push(r)
    groups.set(key, arr)
  }
  return Array.from(groups.values())
    .map(g => g.sort((a, b) => a.startDate.localeCompare(b.startDate)))
    .sort((a, b) => a[0].startDate.localeCompare(b[0].startDate))
}

function AddUnavailabilityModal({ trainerId, onClose, onSaved }: {
  trainerId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [reason, setReason] = useState('')
  const [repeats, setRepeats] = useState(false)
  const [days, setDays] = useState<Set<DayOfWeekName>>(new Set())
  const [recurringEndDate, setRecurringEndDate] = useState('')

  const rangeMutation = useMutation({
    mutationFn: () => adminApi.addTrainerUnavailability(trainerId, { startDate, endDate, reason: reason.trim() || null }),
    onSuccess: onSaved,
  })
  const recurringMutation = useMutation({
    mutationFn: () => adminApi.addTrainerRecurringUnavailability(trainerId, {
      daysOfWeek: [...days], startDate, endDate: recurringEndDate || null, reason: reason.trim() || null,
    }),
    onSuccess: onSaved,
  })

  const toggleDay = (d: DayOfWeekName) =>
    setDays(prev => { const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n })

  const canSave = repeats
    ? !!startDate && days.size > 0 && !recurringMutation.isPending
    : !!startDate && !!endDate && endDate >= startDate && !rangeMutation.isPending

  const inputCls = "h-9 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b">
          <h2 className="text-base font-semibold text-gray-900">Add unavailability</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={repeats} onChange={e => setRepeats(e.target.checked)} className="w-4 h-4" />
            Repeats weekly
          </label>

          {repeats ? (
            <>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">On these days</p>
                <div className="flex gap-1 flex-wrap">
                  {ALL_DAYS.map(d => (
                    <button
                      key={d.name} type="button" onClick={() => toggleDay(d.name)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer transition-colors ${
                        days.has(d.name) ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Until (optional)</label>
                  <input type="date" value={recurringEndDate} onChange={e => setRecurringEndDate(e.target.value)} className={inputCls} />
                </div>
              </div>
              <p className="text-xs text-gray-400">Runs for up to 12 months if no end date is set.</p>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">To</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Reason (optional)</label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Annual leave" className={inputCls} />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer">
            Cancel
          </button>
          <button
            onClick={() => repeats ? recurringMutation.mutate() : rangeMutation.mutate()}
            disabled={!canSave}
            className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {(rangeMutation.isPending || recurringMutation.isPending) ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function AvailabilityTab({ trainerId }: { trainerId: string }) {
  const { formatDate } = useAdminDates()
  const queryClient = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['trainer-unavailability', trainerId],
    queryFn: () => adminApi.getTrainerUnavailability(trainerId).then(r => r.data),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['trainer-unavailability', trainerId] })
  const removeMutation = useMutation({
    mutationFn: (id: string) => adminApi.removeTrainerUnavailability(trainerId, id),
    onSuccess: invalidate,
  })
  const removeGroupMutation = useMutation({
    mutationFn: (groupId: string) => adminApi.removeTrainerUnavailabilityGroup(trainerId, groupId),
    onSuccess: invalidate,
  })

  const groups = groupUnavailability(rows)
  const fmt = (d: string) => formatDate(d, { day: 'numeric', month: 'short', year: 'numeric' })

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" className="gap-1.5" onClick={() => setShowAdd(true)}>
          <Plus size={15} /> Add unavailability
        </Button>
      </div>

      {showAdd && (
        <AddUnavailabilityModal trainerId={trainerId} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); invalidate() }} />
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-sm text-slate-500 p-4">Loading…</p>
          ) : groups.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">No unavailability logged.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {groups.map(g => {
                const first = g[0], last = g[g.length - 1]
                const isRecurring = !!first.recurrenceGroupId
                return (
                  <li key={first.recurrenceGroupId ?? first.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800 flex items-center gap-1.5">
                        <CalendarOff size={13} className="text-slate-400 shrink-0" />
                        {isRecurring
                          ? `Recurring — ${g.length} day${g.length === 1 ? '' : 's'} (${fmt(first.startDate)} – ${fmt(last.startDate)})`
                          : first.startDate === first.endDate
                            ? fmt(first.startDate)
                            : `${fmt(first.startDate)} – ${fmt(first.endDate)}`}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5 ml-5">{first.reason ?? 'No reason given'}</p>
                    </div>
                    <button
                      onClick={() => isRecurring ? removeGroupMutation.mutate(first.recurrenceGroupId!) : removeMutation.mutate(first.id)}
                      disabled={removeMutation.isPending || removeGroupMutation.isPending}
                      className="text-slate-400 hover:text-red-600 cursor-pointer shrink-0 disabled:opacity-50"
                      title="Remove"
                    >
                      <Trash2 size={15} />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Sessions tab ─────────────────────────────────────────────────────────────

function SessionsTab({ trainerId }: { trainerId: string }) {
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['trainer-bookings', trainerId],
    queryFn: () => adminApi.getTrainerBookings(trainerId).then(r => r.data),
  })

  const today = useAdminDates().today()
  const upcoming = bookings.filter(b => b.bookingDate >= today)
  const past     = bookings.filter(b => b.bookingDate <  today)

  if (isLoading) return <p className="text-sm text-slate-500 py-4">Loading…</p>

  if (bookings.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <p className="text-sm text-slate-500">No booking assignments yet.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {upcoming.length > 0 && (
        <BookingSection title="Upcoming" bookings={upcoming} />
      )}
      {past.length > 0 && (
        <BookingSection title="Past" bookings={past} />
      )}
    </div>
  )
}

function BookingSection({ title, bookings }: { title: string; bookings: TrainerBookingDto[] }) {
  const { formatDate } = useAdminDates()
  const fmtDate = (date: string) => {
    return formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {/* Mobile */}
        <ul className="divide-y divide-slate-100 sm:hidden">
          {bookings.map(b => (
            <li key={b.bookingId}>
              <Link
                to={`/admin/bookings/${b.bookingId}`}
                className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{b.courseName}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{b.clientName} · {fmtDate(b.bookingDate)}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <StatusBadge status={b.status} />
                    <span className="text-xs text-slate-400">{b.role}</span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 ml-2" />
              </Link>
            </li>
          ))}
        </ul>

        {/* Desktop */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                <th className="text-left px-4 py-2.5 font-medium">Date</th>
                <th className="text-left px-4 py-2.5 font-medium">Course</th>
                <th className="text-left px-4 py-2.5 font-medium">Client</th>
                <th className="text-left px-4 py-2.5 font-medium">Venue</th>
                <th className="text-left px-4 py-2.5 font-medium">Role</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {bookings.map(b => (
                <tr
                  key={b.bookingId}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => window.location.href = `/admin/bookings/${b.bookingId}`}
                >
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(b.bookingDate)}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {b.courseName}
                    {b.courseCode && <span className="ml-1.5 text-xs text-slate-400">{b.courseCode}</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{b.clientName}</td>
                  <td className="px-4 py-3 text-slate-500">{b.venueName ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{b.role}</td>
                  <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/bookings/${b.bookingId}`}
                      onClick={e => e.stopPropagation()}
                      className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TrainerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = (searchParams.get('tab') as Tab | null) ?? 'details'
  const setTab = (t: Tab) => setSearchParams({ tab: t }, { replace: true })

  const { data: trainer, isLoading, isError } = useQuery({
    queryKey: ['trainer', id],
    queryFn: () => adminApi.getTrainer(id!).then(r => r.data),
    enabled: !!id,
  })

  useTopBar(
    <Breadcrumb items={[
      { label: 'Trainers', to: '/admin/trainers' },
      { label: trainer ? `${trainer.firstName} ${trainer.lastName}` : '…' },
    ]} />
  )

  if (isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading…</div>
  }

  if (isError || !trainer) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600 mb-4">Trainer not found.</p>
        <Button variant="ghost" onClick={() => navigate('/admin/trainers')}>Back to trainers</Button>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {trainer.firstName} {trainer.lastName}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{trainer.email}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {trainer.isActive ? (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
          ) : (
            <Badge className="bg-gray-100 text-gray-500 hover:bg-gray-100">Inactive</Badge>
          )}
          {trainer.hasAccount && (
            <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Has account</Badge>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map(t => {
          const Icon = t.icon
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                tab === t.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Icon size={15} />
              {t.label}
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      {tab === 'details'        && <DetailsTab trainer={trainer} />}
      {tab === 'qualifications' && <QualificationsTab trainer={trainer} />}
      {tab === 'availability'   && <AvailabilityTab trainerId={trainer.id} />}
      {tab === 'sessions'       && <SessionsTab trainerId={trainer.id} />}
    </div>
  )
}
